import { motion, useInView } from "framer-motion"
import { useRef } from "react"
import usePrefersReducedMotion from "hooks/usePrefersReducedMotion"

/**
 * Fades and lifts its children into view once they are scrolled to.
 *
 * Honours the OS "reduce motion" setting via the same hook `atoms/Sparkles.jsx`
 * uses. Under that preference the travel is dropped entirely and the content
 * simply appears — a 150px slide is exactly the kind of large-area movement
 * that setting exists to switch off.
 */
const ScrollAnimate = ({
  children,
  delay = 0.2,
  once = true,
  amount = 0.2,
  className = "",
}) => {
  const ref = useRef(null)
  const inView = useInView(ref, {
    once,
    amount,
  })
  const prefersReducedMotion = usePrefersReducedMotion()

  const animation = {
    // The transitions live inside the variants rather than on the element,
    // because the preference resolves in an effect *after* first paint: the
    // hook reports "reduced" on the server and on the first client render,
    // then corrects itself. That flip changes `y` on the hidden variant, and
    // with a shared transition framer-motion would animate the change — an
    // element sitting off-screen would visibly slide down to its new start
    // position. Holding hidden is therefore instant, and only the entrance
    // itself is timed.
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 150,
      transition: { duration: 0 },
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: prefersReducedMotion
        ? { duration: 0 }
        : { duration: 0.5, delay, ease: "easeOut" },
    },
  }

  return (
    <div ref={ref} className={className}>
      <motion.div
        className="h-full"
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        variants={animation}
      >
        {children}
      </motion.div>
    </div>
  )
}

export default ScrollAnimate
